import {
  Alert,
  Anchor,
  Badge,
  Button,
  Code,
  Divider,
  Group,
  Loader,
  Radio,
  Stack,
  Text,
  Textarea,
  Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import type { ReactElement } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { PromptToolWarning, Role } from "./api";
import { OverrideHistory } from "./OverrideHistory";
import { RoleModelEditor } from "./RoleModelEditor";
import { useResetOverride } from "./useResetOverride";
import { useRoleDetail } from "./useRoleDetail";
import { useSaveOverride } from "./useSaveOverride";
import { agentRoleErrorText } from "./refusalText";

// Detail-редактор одной роли. Два режима:
//   - «default» — эффективный промпт = default из файла плагина. Кнопка
//     «Откатить к default» (DELETE /agent_roles/{role}/prompt)
//     видна/активна только если override существует.
//   - «custom» — Textarea с текстом override'а, кнопка «Сохранить» (PUT
//     /agent_roles/{role}/prompt). Пустой/whitespace текст дизейблит
//     кнопку (бэк вернул бы 422, но до него не доводим).
// Под редактором — общая история override'ов роли, отфильтрованная по
// kind="prompt" (последние 10), и секция выбора модели.

type Mode = "default" | "custom";

function badgeForSource(role: Role): ReactElement | null {
  if (role.prompt_override_source === null) {
    return null;
  }
  return <SourceBadge source={role.prompt_override_source} />;
}

function SourceBadge({
  source,
}: {
  readonly source: NonNullable<Role["prompt_override_source"]>;
}): ReactElement {
  const { t } = useTranslation();
  return <Badge color="grape">{t(`agentRoles.source.${source}`)}</Badge>;
}

// Ссылки на связанные роли в конце развёрнутого промпта. Клик
// разворачивает/подскролливает целевую роль — линейное чтение по
// порядку без возврата к списку.
function RelatedRoles({
  relatedRoles,
  onNavigateToRole,
}: {
  readonly relatedRoles: readonly string[];
  readonly onNavigateToRole: (roleName: string) => void;
}): ReactElement | null {
  const { t } = useTranslation();
  if (relatedRoles.length === 0) {
    return null;
  }
  return (
    <>
      <Divider />
      <Stack gap="xs">
        <Text size="sm" c="dimmed">
          {t("agentRoles.related.hint")}
        </Text>
        <Group gap="md">
          {relatedRoles.map((roleName) => (
            <Anchor
              key={roleName}
              component="button"
              type="button"
              onClick={() => {
                onNavigateToRole(roleName);
              }}
            >
              {t(`agentRoles.role.${roleName}`, { defaultValue: roleName })}
            </Anchor>
          ))}
        </Group>
      </Stack>
    </>
  );
}

function RoleEditorBody({
  role,
  relatedRoles,
  onNavigateToRole,
  onWarnings,
}: {
  readonly role: Role;
  readonly relatedRoles: readonly string[];
  readonly onNavigateToRole: (roleName: string) => void;
  readonly onWarnings: (warnings: readonly PromptToolWarning[]) => void;
}): ReactElement {
  const { t } = useTranslation();
  const saveMutation = useSaveOverride();
  const resetMutation = useResetOverride();

  // Стартовый режим — custom, если override уже есть, иначе default.
  // Тело компонента ремаунтится при смене роли И при изменении
  // override-состояния (key в RoleEditor зависит от
  // effective_system_prompt + has_prompt_override), поэтому локальный
  // draft всегда стартует от свежего эффективного промпта — отдельный
  // useEffect-синк не нужен.
  const [mode, setMode] = useState<Mode>(
    role.has_prompt_override ? "custom" : "default",
  );
  const [draft, setDraft] = useState(role.effective_system_prompt);

  const trimmed = draft.trim();
  const busy = saveMutation.isPending || resetMutation.isPending;

  const handleSave = (): void => {
    saveMutation.mutate(
      { roleName: role.role_name, promptText: draft },
      {
        onSuccess: (saved) => {
          onWarnings(saved.warnings);
          notifications.show({
            message: t("agentRoles.saveSuccess"),
            color: "green",
          });
        },
        onError: (error) => {
          notifications.show({
            message: t("agentRoles.saveError", {
              message: agentRoleErrorText(error, t),
            }),
            color: "red",
          });
        },
      },
    );
  };

  const handleReset = (): void => {
    resetMutation.mutate(role.role_name, {
      onSuccess: () => {
        onWarnings([]);
        notifications.show({
          message: t("agentRoles.resetSuccess"),
          color: "green",
        });
      },
      onError: (error) => {
        notifications.show({
          message: t("agentRoles.resetError", {
            message: agentRoleErrorText(error, t),
          }),
          color: "red",
        });
      },
    });
  };

  return (
    <Stack gap="md">
      <Group gap="xs">
        <Title order={3}>
          {t(`agentRoles.role.${role.role_name}`, {
            defaultValue: role.role_name,
          })}
        </Title>
        {badgeForSource(role)}
      </Group>

      <Radio.Group
        label={t("agentRoles.modeLabel")}
        value={mode}
        onChange={(value) => {
          setMode(value === "custom" ? "custom" : "default");
        }}
      >
        <Group gap="lg">
          <Radio value="default" label={t("agentRoles.modeDefault")} />
          <Radio value="custom" label={t("agentRoles.modeCustom")} />
        </Group>
      </Radio.Group>

      {mode === "default" ? (
        <Stack gap="xs">
          <Text size="sm" c="dimmed">
            {t("agentRoles.defaultPromptLabel")}
          </Text>
          <Code block>{role.default_system_prompt}</Code>
          <Button
            variant="default"
            onClick={handleReset}
            loading={resetMutation.isPending}
            disabled={!role.has_prompt_override || busy}
          >
            {t("agentRoles.resetButton")}
          </Button>
        </Stack>
      ) : (
        <Stack gap="xs">
          <Textarea
            label={t("agentRoles.customPromptLabel")}
            description={t("agentRoles.customPromptHint")}
            rows={10}
            value={draft}
            onChange={(event) => {
              setDraft(event.currentTarget.value);
            }}
          />
          <Group justify="flex-start">
            <Button
              onClick={handleSave}
              loading={saveMutation.isPending}
              disabled={trimmed.length === 0 || busy}
            >
              {t("agentRoles.saveButton")}
            </Button>
          </Group>
        </Stack>
      )}

      <Divider />

      <Stack gap="xs">
        <Title order={4}>{t("agentRoles.history.title")}</Title>
        <OverrideHistory roleName={role.role_name} kind="prompt" />
      </Stack>

      <Divider />

      <RoleModelEditor role={role} />

      <RelatedRoles
        relatedRoles={relatedRoles}
        onNavigateToRole={onNavigateToRole}
      />
    </Stack>
  );
}

// Группа инструментов для одного вида предупреждения в Alert'е.
function WarningGroup({
  hintKey,
  tools,
}: {
  readonly hintKey: string;
  readonly tools: readonly string[];
}): ReactElement | null {
  const { t } = useTranslation();
  if (tools.length === 0) {
    return null;
  }
  return (
    <Stack gap="xs">
      <Text size="sm">{t(hintKey)}</Text>
      <Group gap="xs">
        {tools.map((tool) => (
          <Code key={tool}>{tool}</Code>
        ))}
      </Group>
    </Stack>
  );
}

// Alert о расхождении «промпт ↔ набор инструментов роли»: промпт
// упоминает инструмент вне effective-набора (mentions_unavailable_tool)
// и/или инструмент из эффективного набора не упомянут в промпте
// (tool_not_mentioned). Не блокирующий warning (бэк сохранил override,
// лишь вернул список расхождений).
function PromptWarningsAlert({
  warnings,
}: {
  readonly warnings: readonly PromptToolWarning[];
}): ReactElement | null {
  const { t } = useTranslation();
  if (warnings.length === 0) {
    return null;
  }
  const mentionsUnavailable = warnings
    .filter((warning) => warning.kind === "mentions_unavailable_tool")
    .map((warning) => warning.tool_name);
  const notMentioned = warnings
    .filter((warning) => warning.kind === "tool_not_mentioned")
    .map((warning) => warning.tool_name);
  return (
    <Alert color="orange" title={t("agentRoles.promptWarnings.title")}>
      <Stack gap="xs">
        <WarningGroup
          hintKey="agentRoles.promptWarnings.mentionsUnavailable"
          tools={mentionsUnavailable}
        />
        <WarningGroup
          hintKey="agentRoles.promptWarnings.notMentioned"
          tools={notMentioned}
        />
      </Stack>
    </Alert>
  );
}

// Контент одной роли: держит warn-расхождения из ответа PUT prompt
// (живут здесь, а не в RoleEditorBody — тело ремаунтится по key после
// save и потеряло бы их). Сбрасываются при смене роли — RoleEditor ниже
// key'ит этот контент по roleName, поэтому смена роли ремаунтит и
// обнуляет warnings.
function RoleEditorContent({
  roleName,
  relatedRoles,
  onNavigateToRole,
}: {
  readonly roleName: string;
  readonly relatedRoles: readonly string[];
  readonly onNavigateToRole: (roleName: string) => void;
}): ReactElement {
  const { t } = useTranslation();
  const detail = useRoleDetail(roleName);
  const [warnings, setWarnings] = useState<readonly PromptToolWarning[]>([]);

  if (detail.isLoading) {
    return (
      <Group gap="xs">
        <Loader size="sm" />
        <Text size="sm" c="dimmed">
          {t("agentRoles.detailLoading")}
        </Text>
      </Group>
    );
  }

  if (detail.isError) {
    return (
      <Alert color="red" title={t("agentRoles.detailLoadError")}>
        {agentRoleErrorText(detail.error, t)}
      </Alert>
    );
  }

  if (detail.data === undefined) {
    return <Text c="dimmed">{t("agentRoles.detailLoadError")}</Text>;
  }

  // key включает roleName + override-состояние: при смене роли И после
  // save/reset (меняется effective_system_prompt / has_prompt_override)
  // body ремаунтится, и локальный draft/режим стартуют от свежих данных,
  // а не утекают из предыдущего состояния.
  const data = detail.data;
  return (
    <Stack gap="md">
      <PromptWarningsAlert warnings={warnings} />
      <RoleEditorBody
        key={`${data.role_name}:${String(data.has_prompt_override)}:${data.effective_system_prompt}`}
        role={data}
        relatedRoles={relatedRoles}
        onNavigateToRole={onNavigateToRole}
        onWarnings={setWarnings}
      />
    </Stack>
  );
}

export function RoleEditor({
  roleName,
  relatedRoles,
  onNavigateToRole,
}: {
  readonly roleName: string;
  readonly relatedRoles: readonly string[];
  readonly onNavigateToRole: (roleName: string) => void;
}): ReactElement {
  // key по roleName: смена выбранной роли ремаунтит контент и обнуляет
  // warn-расхождения предыдущей роли (они per-role, не должны утекать).
  return (
    <RoleEditorContent
      key={roleName}
      roleName={roleName}
      relatedRoles={relatedRoles}
      onNavigateToRole={onNavigateToRole}
    />
  );
}
